import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { AbstractStep, ConfiguratorDatastoreService, ConfiguratorScenarioService, CreateScenarioDTO, MarketingScenarioProcess, ScenarioDTO, TransactionalScenarioProcess } from "@tj/tj-dmp-lib-api";
import { ConfirmationService, MessageService } from "primeng/api";
import { Router } from "@angular/router";
import { SelectAffectedPartnersComponent } from "../../../components/select-affected-partners/select-affected-partners.component";
import { AccountService } from "../../../auth/account.service";
import { isAdminOfTenant } from "../../../auth/admin-levels";
import { SelectIconComponent } from "../../../components/select-icon/select-icon.component";

interface ScenarioPresetOption {
    name: string;
    description: string;
    affectedPartners?: string[];
    entryPoint?: string;
    steps?: AbstractStep[];
    icon?: string;
    value: string;
}

@Component({
    selector: "dmp-workflow-creator",
    templateUrl: "./workflow-creator.component.html",
    styleUrls: ["./workflow-creator.component.scss"],
    standalone: false
})
export class WorkflowCreatorComponent implements OnInit {
    @Input() datastore: string;
    @Input() partner: string;
    @Input() scenarios: ScenarioDTO[];
    @Input() isVisible: boolean = false;
    @Output() onClose: EventEmitter<void> = new EventEmitter();

    @ViewChild(SelectIconComponent) iconSettings: SelectIconComponent;
    @ViewChild(SelectAffectedPartnersComponent) affectedPartnersSettings: SelectAffectedPartnersComponent;

    typeOptions = [
        {
            label: this.translate.instant("workflow.type.marketing"),
            value: ScenarioDTO.TypeEnum.MARKETING,
            description: this.translate.instant("workflow.type.description.marketing"),
        },
        {
            label: this.translate.instant("workflow.type.transactional"),
            value: ScenarioDTO.TypeEnum.TRANSACTIONAL,
            description: this.translate.instant("workflow.type.description.transactional"),
        },
    ];
    selectedType: ScenarioDTO.TypeEnum; // Default to marketing type

    defaultPresetOption: ScenarioPresetOption = {
        name: this.translate.instant("workflow.creation.custom.name"),
        description: this.translate.instant("workflow.creation.custom.description"),
        affectedPartners: [],
        entryPoint: null,
        steps: [],
        icon: "pi pi-cog",
        value: 'custom',
    };

    marketingPresetOptions: ScenarioPresetOption[] = [];
    transactionalPresetOptions: ScenarioPresetOption[] = [];
    selectedPreset: ScenarioPresetOption = null;

    form: UntypedFormGroup;

    stepsIndex: number = 0;

    isAdmin: boolean = false;
    isSuperAdmin: boolean = false;
    affectedPartners: string[] = [];
    accessiblePartners: string[] = [];
    possiblePartners: string[] = [];

    constructor(
        private accountService: AccountService,
        private configuratorDatastoreService: ConfiguratorDatastoreService,
        private configuratorScenarioService: ConfiguratorScenarioService,
        private confirmationService: ConfirmationService,
        private fb: UntypedFormBuilder,
        private messageService: MessageService,
        private router: Router,
        private translate: TranslateService
    ) { }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    get currentCode() {
        return ("WF_" + ((this.form.value.name as string) ?? "").toUpperCase().replace(/ /g, "_"));
    }

    get isCreateButtonDisabled(): boolean {
        return this.form?.invalid;
    }

    ngOnInit(): void {
        this.accountService.fetch().subscribe(account => {
            this.isAdmin = isAdminOfTenant(account, this.datastore);
            this.isSuperAdmin = account.admin;

            // Initialization logic here
            this.initPartners();
            this.initOptions();
        });
    }

    initPartners(): void {
        this.configuratorDatastoreService.getDatastoreAccessiblePartnersUsingGET(this.datastore, true).subscribe(partners => {
            this.accessiblePartners = partners;
        });
    }

    initOptions(): void {
        this.configuratorScenarioService.listOrLatest(this.datastore, this.partner, null, true).subscribe((scenarios) => {
            if(!this.isSuperAdmin) {
                this.marketingPresetOptions = scenarios.filter(scenario => (scenario.type === ScenarioDTO.TypeEnum.MARKETING && scenario.affectedPartners.includes(this.partner))).map(scenario => ({
                    name: scenario.name,
                    description: scenario.description,
                    affectedPartners: scenario.affectedPartners,
                    value: scenario.scenarioId,
                    icon: scenario.icon,
                    steps: scenario.steps ? scenario.steps : [],
                }));
                this.transactionalPresetOptions = scenarios.filter(scenario => (scenario.type === ScenarioDTO.TypeEnum.TRANSACTIONAL && scenario.affectedPartners.includes(this.partner))).map(scenario => ({
                    name: scenario.name,
                    description: scenario.description,
                    affectedPartners: scenario.affectedPartners,
                    value: scenario.scenarioId,
                    icon: scenario.icon,
                    entryPoint: (scenario.process as TransactionalScenarioProcess).entryPoint,
                    steps: scenario.steps ? scenario.steps : [],
                }));
            }
            this.marketingPresetOptions.unshift(this.defaultPresetOption);
            this.transactionalPresetOptions.unshift(this.defaultPresetOption);
        });
    }

    changeSelectedType(type: ScenarioDTO.TypeEnum): void {
        if (type === ScenarioDTO.TypeEnum.MARKETING) {
            this.selectedPreset = null;
        }
        this.selectedType = type;
    }

    changeDefaultScenario(scenario: ScenarioPresetOption): void {
        this.selectedPreset = scenario;
        if (this.form) {
            const isCustom = scenario.value === 'custom';
            if (this.selectedType === ScenarioDTO.TypeEnum.MARKETING) {
                this.form.patchValue({
                    partner: this.partner,
                    steps: scenario.steps,
                });
            } else if (this.selectedType === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
                this.form.patchValue({
                    partner: this.partner,
                    entryPoint: scenario.entryPoint ? scenario.entryPoint : null,
                    steps: scenario.steps,
                });
                if(!isCustom) {
                    this.form.get('entryPoint')?.disable();
                }
            }
        }
    }

    createWorkflow(): void {
        // Logic to create the workflow
        const value = this.form.getRawValue();
        if (this.scenarios.filter((data) => data.name === value.name).length > 0) {
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant("workflow.error.already-exists"),
            });
            return;
        }
        this.confirmationService.confirm({
            message: this.translate.instant("workflow.confirmation.confirm-creation"),
            icon: "pi pi-exclamation-triangle",
            accept: () => {
                let scenarioProcess: MarketingScenarioProcess | TransactionalScenarioProcess;
                if (this.selectedType === ScenarioDTO.TypeEnum.MARKETING) {
                    scenarioProcess = {
                        type: 'MarketingScenario'
                    } as MarketingScenarioProcess;
                } else if (this.selectedType === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
                    scenarioProcess = {
                        entryPoint: value.entryPoint,
                        type: 'TransactionalScenario'
                    } as TransactionalScenarioProcess;
                }
                const newScenario: CreateScenarioDTO = {
                    name: value.name,
                    affiliateCode: this.isAdmin && !value.partner ? "all" : value.partner,
                    description: value.description,
                    owner: this.isAdmin ? "admin" : value.partner,
                    icon: value.icon,
                    type: this.selectedType,
                    process: scenarioProcess,
                    affectedPartners: this.affectedPartners,
                }
                this.configuratorScenarioService.create(newScenario, this.datastore).subscribe((createdScenario) => {
                    this.configuratorScenarioService.updateSteps(value.steps, this.datastore, createdScenario.scenarioId, createdScenario.version).subscribe(() => {
                        this.router.navigate(['/h/workflow/board', this.datastore, createdScenario.scenarioId, 'edit'], {
                            queryParams: { version: createdScenario.version }
                        });
                    });
                });
            }
        });
    }

    /**
     * Sortie de la modale : bouton « Annuler », croix en haut à droite et clic sur le masque
     * passent tous par ici. PrimeNG masque déjà la modale avant d'émettre `(onHide)`, donc si
     * l'utilisateur renonce à abandonner sa saisie il faut la réafficher — sans quoi elle
     * resterait invisible alors que la création est toujours en cours.
     */
    closeDialog(): void {
        this.confirmationService.confirm({
            message: this.translate.instant("workflow.confirmation.cancel-creation"),
            accept: () => {
                this.onClose.emit();
            },
            reject: () => {
                this.isVisible = true;
            },
        });
    }

    previousStep(): void {
        this.stepsIndex--;
    }

    nextStep(): void {
        if (this.stepsIndex === 0) {
            let formConfig: any = {
                name: [null, Validators.required],
                description: [null],
                partner: [null, Validators.required],
                icon: [null],
                steps: [[]]
            };
            if (this.selectedType === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
                formConfig = {
                    ...formConfig,
                    entryPoint: [null, Validators.required]
                }
            }
            this.form = this.fb.group(formConfig);

            // Watch partner field changes and update affectedPartners
            this.form.get('partner')?.valueChanges.subscribe(partnerValue => {
                if (partnerValue) {
                    this.affectedPartners = [partnerValue];
                } else {
                    this.affectedPartners = [];
                }
            });
        }
        this.stepsIndex++;
    }

    updateAffectedPartners(affectedPartners: string[]) {
        this.affectedPartners = affectedPartners;
    }

    updateIcon(icon: string) {
        this.form.patchValue({ icon });
    }
}
